import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from 'antd';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getModalData, getModaOpenModalId } from '@/store/modal/selector';
import { closeModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';

interface NotesModalData {
	initialNote?: string;
	title: string;
	onSave: (note: string) => Promise<void>;
}

export const NotesModal: React.FC = () => {
	const dispatch = useAppDispatch();
	const activeModalId = useAppSelector(getModaOpenModalId);
	const modalData = useAppSelector(getModalData) as NotesModalData | undefined;

	const isOpen = activeModalId === MODAL_IDS.NOTES_MODAL;
	const { initialNote = '', title, onSave } = modalData || {};

	const [noteText, setNoteText] = useState(initialNote);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (isOpen && modalData) {
			setNoteText(modalData.initialNote || '');
			setIsSaving(false);
		}
	}, [isOpen, modalData]);

	const handleSave = () => {
		if (onSave) {
			setIsSaving(true);
			onSave(noteText.trim()).catch(() => {
				setIsSaving(false);
			});
		}
	};

	const handleCancel = () => {
		dispatch(closeModal());
	};

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			title={title}
			open={isOpen}
			onCancel={handleCancel}
			footer={[
				<Button key="back" onClick={handleCancel} disabled={isSaving}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					loading={isSaving}
					onClick={handleSave}
					disabled={noteText.trim() === initialNote?.trim() || isSaving}
				>
					Save Note
				</Button>,
			]}
		>
			<Input.TextArea
				rows={6}
				value={noteText}
				onChange={(e) => setNoteText(e.target.value)}
				placeholder="Add an internal note for this response..."
				disabled={isSaving}
			/>
		</Modal>
	);
};
